import { encode } from 'querystring';

import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { omitNullish } from '@helpers/data';

import { BaseNetError } from '@errors/base-net-error';

import { baseNetErrorBodyFromDto } from '@mapping/errors';

import { HttpFetchConfig, HttpFetchHeaders, HttpFetchResult } from './types';

export const httpFetch = <
  R,
  D = undefined,
  P = undefined,
  H extends HttpFetchHeaders = undefined
>(
  url: string,
  config?: HttpFetchConfig<D, P, H>
): HttpFetchResult<R> => {
  const source = axios.CancelToken.source();
  const { token: cancelToken } = source;

  const nextConfig: AxiosRequestConfig = {
    cancelToken,
    paramsSerializer: {
      serialize: (params) => encode(omitNullish(params)),
    },
    validateStatus: (status) => status >= 200 && status < 300,
    ...config,
    headers: config?.headers,
    url,
    onUploadProgress: (event) => {
      if (!config?.onUploadProgress) {
        return;
      }

      if (!event.total) {
        return;
      }

      const progress = Math.round((event.loaded * 100) / event.total);
      config?.onUploadProgress?.(progress, event.event);
    },
  };

  return {
    promise: axios //
      .request<R>(nextConfig)
      .catch((error: AxiosError<R, D>) => {
        if (axios.isCancel(error)) {
          console.warn(`Request canceled: ${error.message}`);
          throw error;
        }

        throw error;
      })
      .catch((error: AxiosError<R, D>) => {
        if (error.response) {
          const { status, statusText, data } = error.response;

          throw new BaseNetError(
            status,
            statusText,
            baseNetErrorBodyFromDto(data)
          );
        }

        throw error;
      }),
    cancel: source.cancel,
  };
};
